import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useApp } from '../contexts/AppContext';
import {
  Network, Search, RefreshCw, Server, Wifi, WifiOff,
  ShieldAlert, Edit2, Trash2, Play, Loader2, ChevronLeft, ChevronRight, Filter,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PAGE_SIZE_OPTIONS = [10, 100, 300, 500, 1000, 2000];

const IPAllocations = () => {
  const { user } = useAuth();
  const { selectedBranch } = useApp();
  const branchQuery = (selectedBranch && selectedBranch !== 'all') ? selectedBranch : '';
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [pageSize, setPageSize] = useState(100);
  const [stats, setStats] = useState(null);
  const [routers, setRouters] = useState([]);
  const [filterRouter, setFilterRouter] = useState('all');
  const [filterIsolated, setFilterIsolated] = useState('all');
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [reassignOpen, setReassignOpen] = useState(false);
  const [reassignRow, setReassignRow] = useState(null);
  const [reassignIP, setReassignIP] = useState('');
  const [reassignBusy, setReassignBusy] = useState(false);
  const [revokeBusy, setRevokeBusy] = useState({});
  const [bootstrapBusy, setBootstrapBusy] = useState(false);

  // Debounce search
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 350);
    return () => clearTimeout(t);
  }, [search]);

  const authHeader = useCallback(() => {
    const token = localStorage.getItem('access_token') || localStorage.getItem('token');
    return { Authorization: `Bearer ${token}` };
  }, []);

  const loadStats = useCallback(async () => {
    try {
      const params = branchQuery ? `?branch_id=${encodeURIComponent(branchQuery)}` : '';
      const r = await axios.get(`${API_URL}/api/v2/ip-allocations/stats${params}`, { headers: authHeader() });
      if (r.data?.success) setStats(r.data.data);
    } catch (e) {
      console.error('stats failed', e);
    }
  }, [authHeader, branchQuery]);

  const loadRouters = useCallback(async () => {
    try {
      const params = branchQuery ? `?branch_id=${encodeURIComponent(branchQuery)}&limit=200` : '?limit=200';
      const r = await axios.get(`${API_URL}/api/v2/mikrotiks${params}`, { headers: authHeader() });
      const list = r.data?.data || r.data || [];
      setRouters(Array.isArray(list) ? list : []);
    } catch (e) {
      console.error('routers failed', e);
    }
  }, [authHeader, branchQuery]);

  const loadItems = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set('limit', pageSize);
      params.set('offset', offset);
      if (filterRouter && filterRouter !== 'all') params.set('router_id', filterRouter);
      if (branchQuery) params.set('branch_id', branchQuery);
      if (filterIsolated && filterIsolated !== 'all') params.set('isolated', filterIsolated);
      if (debouncedSearch) params.set('search', debouncedSearch);
      const r = await axios.get(`${API_URL}/api/v2/ip-allocations?${params}`, { headers: authHeader() });
      if (r.data?.success) {
        setItems(r.data.data || []);
        setTotal(r.data.total || 0);
      }
    } catch (e) {
      toast.error('Gagal memuat daftar alokasi IP');
    } finally {
      setLoading(false);
    }
  }, [authHeader, offset, pageSize, filterRouter, filterIsolated, debouncedSearch, branchQuery]);

  useEffect(() => { loadStats(); loadRouters(); }, [loadStats, loadRouters]);
  useEffect(() => { loadItems(); }, [loadItems]);

  const handleReassign = async () => {
    if (!reassignRow || !reassignIP) return;
    setReassignBusy(true);
    try {
      const r = await axios.post(
        `${API_URL}/api/v2/ip-allocations/${reassignRow.customer_id}/reassign`,
        { ip_address: reassignIP, push_to_radreply: true },
        { headers: authHeader() },
      );
      if (r.data?.success) {
        toast.success(`IP ${reassignRow.name} diubah jadi ${reassignIP}. Kick sesi PPPoE agar CPE reconnect dan pakai IP baru.`);
        setReassignOpen(false);
        setReassignRow(null);
        setReassignIP('');
        loadItems();
        loadStats();
      }
    } catch (e) {
      const msg = e.response?.data?.detail || 'Gagal reassign IP';
      toast.error(String(msg));
    } finally {
      setReassignBusy(false);
    }
  };

  const handleRevoke = async (row) => {
    if (!window.confirm(`Cabut alokasi IP ${row.ip_address} dari ${row.name}? IP akan bebas untuk pelanggan lain, dan radreply akan dibersihkan.`)) return;
    setRevokeBusy(prev => ({ ...prev, [row.customer_id]: true }));
    try {
      const r = await axios.post(
        `${API_URL}/api/v2/ip-allocations/${row.customer_id}/revoke`,
        { reason: 'admin_manual', clear_radreply: true },
        { headers: authHeader() },
      );
      if (r.data?.success) {
        toast.success('Alokasi dicabut');
        loadItems();
        loadStats();
      }
    } catch (e) {
      toast.error('Gagal mencabut alokasi');
    } finally {
      setRevokeBusy(prev => ({ ...prev, [row.customer_id]: false }));
    }
  };

  const handleAllocate = async (row) => {
    try {
      const r = await axios.post(
        `${API_URL}/api/v2/ip-allocations/${row.customer_id}/allocate`,
        {},
        { headers: authHeader() },
      );
      if (r.data?.success) {
        toast.success(`IP dialokasikan: ${r.data.data.ip || 'oke'}`);
        loadItems();
        loadStats();
      } else {
        toast.error(r.data?.data?.error || 'Gagal alokasi');
      }
    } catch (e) {
      toast.error('Gagal alokasi IP');
    }
  };

  const handleBulkBootstrap = async () => {
    if (filterRouter === 'all') {
      toast.error('Pilih router dulu di filter, lalu klik Bootstrap');
      return;
    }
    if (!window.confirm('Alokasikan IP untuk SEMUA pelanggan PPPoE di router ini? Idempotent (yang sudah punya IP tidak berubah).')) return;
    setBootstrapBusy(true);
    try {
      const r = await axios.post(
        `${API_URL}/api/v2/ip-allocations/bulk-bootstrap/${filterRouter}`,
        {},
        { headers: authHeader() },
      );
      if (r.data?.success) {
        const s = r.data.stats;
        toast.success(`Bootstrap selesai: ${s.ok} OK, ${s.err} error, ${s.isolir} isolir dari ${r.data.total_customers} pelanggan.`);
        loadItems();
        loadStats();
      }
    } catch (e) {
      toast.error('Gagal bootstrap');
    } finally {
      setBootstrapBusy(false);
    }
  };

  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const currentPage = Math.floor(offset / pageSize) + 1;

  return (
    <div className="p-4 lg:p-6 space-y-6" data-testid="ip-allocations-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2 text-slate-800">
            <Network className="w-6 h-6 text-cyan-600" />
            Alokasi IP Pelanggan
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            IP unik per pelanggan lewat RADIUS <code className="text-xs bg-slate-100 px-1 py-0.5 rounded">Framed-IP-Address</code>. Isolir tetap pakai <code className="text-xs bg-slate-100 px-1 py-0.5 rounded">pool-isolir</code>.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => { loadStats(); loadItems(); }} data-testid="refresh-btn">
            <RefreshCw className="w-4 h-4 mr-2" /> Refresh
          </Button>
          <Button
            onClick={handleBulkBootstrap}
            disabled={bootstrapBusy || filterRouter === 'all'}
            data-testid="bulk-bootstrap-btn"
          >
            {bootstrapBusy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Play className="w-4 h-4 mr-2" />}
            Bootstrap Router
          </Button>
        </div>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <StatCard label="Total IP Dialokasikan" value={stats.total_allocations} icon={<Network className="w-4 h-4" />} color="cyan" testId="stat-total" />
          <StatCard label="Slot /24 Terpakai" value={`${stats.bank?.used ?? 0} / ${stats.bank?.total ?? 0}`} icon={<Server className="w-4 h-4" />} color="blue" testId="stat-bank" />
          <StatCard label="Slot Bank Tersedia" value={stats.bank?.free ?? 0} icon={<Server className="w-4 h-4" />} color="emerald" testId="stat-bank-free" />
          <StatCard label="Router Aktif" value={stats.per_router?.length ?? 0} icon={<Server className="w-4 h-4" />} color="violet" testId="stat-routers" />
        </div>
      )}

      {/* Per-router breakdown */}
      {stats?.per_router?.length > 0 && (
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <div className="text-sm font-semibold text-slate-700 mb-3">Alokasi per Router</div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {stats.per_router.map(r => (
              <div key={r.router_id} className="flex items-center justify-between bg-slate-50 rounded-lg px-3 py-2 border border-slate-100">
                <div>
                  <div className="text-sm font-medium text-slate-700">{r.router_name || `Router #${r.router_id}`}</div>
                  <div className="text-xs text-slate-500">{r.allocated} IP · {r.isolated} isolir</div>
                </div>
                <Button size="sm" variant="ghost" onClick={() => { setFilterRouter(String(r.router_id)); setOffset(0); }} data-testid={`filter-router-${r.router_id}`}>
                  Lihat
                </Button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white rounded-xl border border-slate-200 p-4 flex flex-wrap gap-3 items-center">
        <div className="flex items-center gap-2 text-sm text-slate-500">
          <Filter className="w-4 h-4" />
          <span>Filter</span>
        </div>
        <div className="w-full md:w-64">
          <Select value={filterRouter} onValueChange={(v) => { setFilterRouter(v); setOffset(0); }}>
            <SelectTrigger data-testid="filter-router-select"><SelectValue placeholder="Semua Router" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Semua Router</SelectItem>
              {routers.map(r => (
                <SelectItem key={r.id} value={String(r.id)}>{r.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="w-full md:w-48">
          <Select value={filterIsolated} onValueChange={(v) => { setFilterIsolated(v); setOffset(0); }}>
            <SelectTrigger data-testid="filter-isolir-select"><SelectValue placeholder="Semua Status" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Semua Status</SelectItem>
              <SelectItem value="false">Aktif (Non-isolir)</SelectItem>
              <SelectItem value="true">Isolir</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="relative flex-1 min-w-[220px]">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input
            className="pl-9"
            placeholder="Cari nama / akun / username / IP"
            value={search}
            onChange={(e) => { setSearch(e.target.value); setOffset(0); }}
            data-testid="search-input"
          />
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-slate-500 whitespace-nowrap">Tampilan entri</span>
          <div className="w-24">
            <Select value={String(pageSize)} onValueChange={(v) => { setPageSize(Number(v)); setOffset(0); }}>
              <SelectTrigger data-testid="page-size-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {PAGE_SIZE_OPTIONS.map(n => (
                  <SelectItem key={n} value={String(n)}>{n}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left">
              <tr className="text-xs font-semibold text-slate-600 uppercase tracking-wide">
                <th className="px-4 py-3">Pelanggan</th>
                <th className="px-4 py-3">Router</th>
                <th className="px-4 py-3">Subnet</th>
                <th className="px-4 py-3">IP Address</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Assigned</th>
                <th className="px-4 py-3 text-right">Aksi</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan="7" className="px-4 py-8 text-center text-slate-400">
                  <Loader2 className="w-5 h-5 animate-spin inline mr-2" /> Memuat...
                </td></tr>
              )}
              {!loading && items.length === 0 && (
                <tr><td colSpan="7" className="px-4 py-8 text-center text-slate-400">
                  Tidak ada alokasi. Klik <b>Bootstrap Router</b> untuk mengalokasikan.
                </td></tr>
              )}
              {!loading && items.map(row => (
                <tr key={row.id} className="border-t border-slate-100 hover:bg-slate-50/50" data-testid={`row-${row.customer_id}`}>
                  <td className="px-4 py-3">
                    <div className="font-medium text-slate-800">{row.name}</div>
                    <div className="text-xs text-slate-500">
                      {row.account_number} · <code className="bg-slate-100 px-1 rounded">{row.pppoe_username}</code>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-slate-600">{row.router_name || '-'}</td>
                  <td className="px-4 py-3">
                    <code className="text-xs bg-slate-100 px-2 py-0.5 rounded">{row.subnet_cidr}</code>
                  </td>
                  <td className="px-4 py-3">
                    <code className="font-mono font-semibold text-cyan-700">{row.ip_address}</code>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex gap-1 flex-wrap">
                      {row.is_isolated ? (
                        <Badge className="bg-red-500/10 text-red-700 border-red-200 gap-1">
                          <ShieldAlert className="w-3 h-3" /> Isolir
                        </Badge>
                      ) : (
                        <Badge className="bg-emerald-500/10 text-emerald-700 border-emerald-200">Aktif</Badge>
                      )}
                      {row.is_online ? (
                        <Badge className="bg-cyan-500/10 text-cyan-700 border-cyan-200 gap-1">
                          <Wifi className="w-3 h-3" /> Online
                        </Badge>
                      ) : (
                        <Badge className="bg-slate-200 text-slate-500 gap-1">
                          <WifiOff className="w-3 h-3" /> Offline
                        </Badge>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-xs text-slate-500">
                    {row.assigned_at ? new Date(row.assigned_at).toLocaleString('id-ID') : '-'}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex gap-1 justify-end">
                      <Button
                        size="sm" variant="ghost"
                        onClick={() => { setReassignRow(row); setReassignIP(row.ip_address); setReassignOpen(true); }}
                        data-testid={`reassign-btn-${row.customer_id}`}
                      >
                        <Edit2 className="w-4 h-4" />
                      </Button>
                      <Button
                        size="sm" variant="ghost"
                        onClick={() => handleRevoke(row)}
                        disabled={!!revokeBusy[row.customer_id]}
                        data-testid={`revoke-btn-${row.customer_id}`}
                      >
                        {revokeBusy[row.customer_id]
                          ? <Loader2 className="w-4 h-4 animate-spin" />
                          : <Trash2 className="w-4 h-4 text-red-500" />}
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {total > pageSize && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-slate-100 bg-slate-50">
            <div className="text-xs text-slate-500">
              Halaman {currentPage} dari {totalPages} · {total} total
            </div>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - pageSize))}>
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <Button size="sm" variant="outline" disabled={offset + pageSize >= total} onClick={() => setOffset(offset + pageSize)}>
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Reassign Dialog */}
      <Dialog open={reassignOpen} onOpenChange={setReassignOpen}>
        <DialogContent data-testid="reassign-dialog">
          <DialogHeader>
            <DialogTitle>Reassign IP</DialogTitle>
          </DialogHeader>
          {reassignRow && (
            <div className="space-y-3 text-sm">
              <div>
                <div className="text-slate-500 text-xs">Pelanggan</div>
                <div className="font-medium">{reassignRow.name} <span className="text-slate-400">({reassignRow.pppoe_username})</span></div>
              </div>
              <div>
                <div className="text-slate-500 text-xs">IP Saat Ini</div>
                <code className="font-mono text-cyan-700">{reassignRow.ip_address}</code>
              </div>
              <div>
                <div className="text-slate-500 text-xs">Subnet Router</div>
                <code className="text-xs bg-slate-100 px-2 py-0.5 rounded">{reassignRow.subnet_cidr}</code>
              </div>
              <div>
                <label className="text-slate-600 text-xs">IP Baru</label>
                <Input
                  value={reassignIP}
                  onChange={(e) => setReassignIP(e.target.value.trim())}
                  placeholder="mis. 172.16.0.100"
                  data-testid="reassign-ip-input"
                />
                <p className="text-xs text-slate-400 mt-1">
                  Harus di dalam subnet router. IP yang sudah dipakai pelanggan lain akan ditolak. Setelah reassign, kick sesi PPPoE-nya agar CPE reconnect dengan IP baru.
                </p>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setReassignOpen(false)}>Batal</Button>
            <Button onClick={handleReassign} disabled={reassignBusy || !reassignIP} data-testid="reassign-confirm-btn">
              {reassignBusy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Simpan
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const StatCard = ({ label, value, icon, color, testId }) => (
  <div className={`bg-white rounded-xl border border-slate-200 p-4`} data-testid={testId}>
    <div className={`inline-flex items-center gap-1.5 text-xs font-medium text-${color}-700 bg-${color}-50 px-2 py-1 rounded-md`}>
      {icon} <span>{label}</span>
    </div>
    <div className="mt-2 text-2xl font-bold text-slate-800">{value}</div>
  </div>
);

export default IPAllocations;
